import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { css } from '@emotion/react';
import { debounce } from 'lodash';
import { IconName, Option, ProColors, Select } from '../../aura';
import { useFormContext } from '../../state';
import { useIsMounted } from '../../hooks';
import {
  getPlaceAddress,
  getPlacePredictions,
  initGoogleMapsAPI,
} from '../../features/google-maps';

function AutocompleteResult(props: {
  prediction: google.maps.places.AutocompletePrediction;
}): JSX.Element {
  const { prediction } = props;

  return (
    <div>
      <div
        className="aura--copy-normal"
        css={css({ color: ProColors.navy[700] })}
      >
        {prediction.structured_formatting.main_text}
      </div>
      <div
        className="aura--copy-normal"
        css={css({ color: ProColors.navy[400] })}
      >
        {prediction.structured_formatting.secondary_text}
      </div>
    </div>
  );
}

// Styled as a link, but the whole option row is what selects it.
function AddAnyway() {
  const { t } = useTranslation('stepOneSearch');

  return (
    <div
      className="aura--copy-sm"
      css={css({
        color: ProColors.navy[800],
        fontWeight: 400,
        whiteSpace: 'pre-wrap',
        display: 'flex',
      })}
    >
      <Trans
        t={t}
        i18nKey="addManually"
        components={[<span className="aura--link aura--link-sm" />]}
      />
    </div>
  );
}

function useOptions(
  query: string,
): Option<google.maps.places.AutocompletePrediction | null | undefined>[] {
  const [options, setOptions] = useState<
    Option<google.maps.places.AutocompletePrediction | null | undefined>[]
  >([]);
  const ref = useRef<{ query: string; resultsQuery: string }>({
    query: '',
    resultsQuery: '',
  });
  const isMounted = useIsMounted();

  const tryUpdateOptions = useCallback(
    async (q: string) => {
      const predictions = await getPlacePredictions(q);
      if (!isMounted.current) {
        return;
      }

      if (
        // response does not match the current query
        !ref.current.query.startsWith(q) ||
        // is further away than the last response ?
        Math.abs(q.length - ref.current.query.length) >
          Math.abs(ref.current.resultsQuery.length - ref.current.query.length)
      ) {
        return;
      }

      setOptions(
        predictions.map(p => ({
          label: p.place_id,
          value: p,
          key: p.place_id,
          component: <AutocompleteResult prediction={p} />,
        })),
      );
      ref.current.resultsQuery = q;
    },
    [isMounted],
  );

  useEffect(() => {
    ref.current.query = query;

    if (query) {
      tryUpdateOptions(query);
    } else {
      setOptions([]);
      ref.current.resultsQuery = query;
    }
  }, [query, tryUpdateOptions]);

  return [
    ...options.slice(0, 3),
    {
      label: '',
      value: null,
      key: 'add_anyway',
      component: <AddAnyway />,
    },
  ];
}

export type SearchBusinessProps = {
  closeSearch: (addInfoMode?: 'manual' | 'automatic') => void;
};

export function SearchBusiness(props: SearchBusinessProps): JSX.Element {
  const { t } = useTranslation('input', { keyPrefix: 'searchBusiness' });
  const { setData } = useFormContext();
  const [query, setQuery] = useState<string>('');
  // `query` trails the input by the debounce; adding manually saves what was typed.
  const typed = useRef('');
  const { closeSearch } = props;

  // Load Maps when the search is shown rather than on every /join visit
  useEffect(() => {
    initGoogleMapsAPI();
  }, []);

  const onAddAnyway = useCallback(() => {
    setData(current => {
      return {
        ...current,
        businessName: typed.current,
      };
    });
    closeSearch('manual');
  }, [closeSearch, setData]);

  const onValueChange = useCallback(
    async (
      value: Option<
        google.maps.places.AutocompletePrediction | null | undefined
      >,
    ) => {
      if (!value.value) {
        onAddAnyway();
        return;
      }

      const address = await getPlaceAddress(value.value.place_id);
      setData(current => {
        return {
          ...current,
          businessName: value.value!.structured_formatting.main_text,
          address,
          placeId: value.value?.place_id,
        };
      });
      closeSearch('automatic');
    },
    [closeSearch, onAddAnyway, setData],
  );

  const setQueryDebounced = useMemo(() => debounce(setQuery, 300), []);
  const onInputChange = useCallback(
    (q: string) => {
      typed.current = q;
      setQueryDebounced(q);
    },
    [setQueryDebounced],
  );

  const options = useOptions(query);
  const value = useMemo(
    () => ({ label: query, value: undefined, key: 'default' }),
    [query],
  );

  return (
    <Select
      id="search-business"
      label={t('label')}
      iconName={IconName.TW_PRO_VENUE_SEARCH}
      options={options}
      value={value}
      onChange={onValueChange}
      filterOptionsFn={() => true}
      onInputChange={onInputChange}
      autoClearInput={false}
      css={css({
        '.aura--select-options': { maxHeight: 325 },
        '.aura--select-options-section': { height: 320 },
      })}
    />
  );
}
